/**
 * Spec 109 §6 — the persona diagnosis SUMMARY view (`/persona-diagnoses`).
 *
 * Across the persona's query clusters: healthy vs diagnosed, RANKED by severity x
 * confidence (clarify Q4) so the most damning, best-evidenced finding is on top.
 * Each card shows the cluster, the diagnosis_type label, and a confidence chip,
 * and links to the full deliverable.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';
import { useBrand } from '../lib/brand-context.js';
import { useAuth } from '../lib/auth-context.js';
import { useToast } from '../hooks/useToast.js';
import {
  listPersonaDiagnosesRanked,
  createPersonaDiagnosis,
  DIAGNOSIS_TYPE_LABEL,
} from '../api/personaDiagnoses.js';
import type { PersonaDiagnosisRankedItem } from '../api/personaDiagnoses.js';
import { listPersonas } from '../api/personas.js';
import type { Persona } from '../api/personas.js';
import {
  listFanoutClusters,
  getFanoutCluster,
  type FanoutClusterRow,
} from '../api/fanout.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { EmptyState } from '../components/EmptyState.js';
import { Select } from '../components/Select.js';
import { RowList, Row, RowFacts, RowFact } from '../components/ui/Row.js';
import { Chip } from '../components/ui/Chip.js';
import type { ChipProps } from '../components/ui/Chip.js';

// Spec 235 task 3 — the chip-tone mapping (evidence.md): emerald -> ok,
// amber -> warn, rose -> fail.
const CONFIDENCE_TONE: Record<string, ChipProps['tone']> = {
  high: 'ok',
  moderate: 'warn',
  low: 'warn',
  very_low: 'fail',
};

// Spec 235 — this card was a whole-card <Link>, the exact shape Row.tsx's
// header comment exists to replace: a bare "Cluster xxxxxxxx · observed
// visibility 62% · deliverable ready" middle-dot string in one <div>, no
// slot of its own for any of the three facts it packs together. `label` (the
// diagnosis type, or "Healthy") is genuinely the row's identity, so it
// becomes `title`; the three facts each get their own RowFact.
//
// The dropped border-l-4 accent turned out NOT to be chrome: it encoded a
// real three-way distinction (healthy / audience_mismatch / any other
// diagnosis type) that nothing else on the row carried at a glance, so
// removing it was information loss, not a chrome trim — this spec's whole
// argument is that CONTENT survives while only chrome changes, and severity
// is content. Restored below as a Chip in `top`, not a colour alone: spec
// 234 measured five engine colours as indistinguishable at dE 4.0 for
// normal vision, and a colour-keyed legend could not rescue identity there
// either. The label (the diagnosis type's own name, or "Healthy") is the
// reliable identity; the tone is reinforcement on top of it, never the only
// signal — which is also why it stays a short, specific label rather than a
// generic "Diagnosed", so two different diagnosis types sharing the "warn"
// tone are still distinguishable by name alone.
function DiagnosisCard({ item }: { item: PersonaDiagnosisRankedItem }) {
  const label = item.diagnosis_type
    ? DIAGNOSIS_TYPE_LABEL[item.diagnosis_type]
    : item.status === 'completed'
      ? 'Healthy — no diagnosis needed'
      : `Status: ${item.status}`;
  const severityLabel = item.diagnosis_type
    ? DIAGNOSIS_TYPE_LABEL[item.diagnosis_type].split(':')[0]
    : 'Healthy';
  const severityTone = !item.diagnosis_type
    ? 'ok'
    : item.diagnosis_type === 'audience_mismatch'
      ? 'fail'
      : 'warn';
  return (
    <Row
      to={`/persona-diagnoses/${item.id}`}
      data-testid="diagnosis-card"
      data-diagnosis-type={item.diagnosis_type ?? 'healthy'}
      data-rank-score={item.rank_score.toFixed(4)}
      title={label}
      top={
        <>
          <Chip tone={severityTone} data-testid="diagnosis-card-severity">
            {severityLabel}
          </Chip>
          {item.combined_confidence ? (
            <Chip
              tone={CONFIDENCE_TONE[item.combined_confidence]}
              data-testid="diagnosis-card-confidence"
              className="capitalize"
            >
              {item.combined_confidence}
            </Chip>
          ) : null}
        </>
      }
    >
      <RowFacts>
        <RowFact icon="bx-collection">Cluster {item.query_cluster_id.slice(0, 8)}</RowFact>
        <RowFact icon="bx-show">
          {item.observed_visibility != null
            ? `${Math.round(item.observed_visibility * 100)}% observed visibility`
            : 'Observed visibility —'}
        </RowFact>
        <RowFact icon={item.has_narrative ? 'bx-check-circle' : 'bx-time-five'}>
          {item.has_narrative ? 'Deliverable ready' : 'Awaiting synthesis'}
        </RowFact>
      </RowFacts>
    </Row>
  );
}

export function PersonaDiagnosisPage() {
  const { selectedBrand } = useBrand();
  const { hasRole } = useAuth();
  const { addToast } = useToast();
  const brandId = selectedBrand?.id ?? null;

  const canRun = useMemo(
    () => hasRole('rule_admin') || hasRole('orbit:admin'),
    [hasRole],
  );

  const [items, setItems] = useState<PersonaDiagnosisRankedItem[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [clusters, setClusters] = useState<FanoutClusterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPersona, setSelectedPersona] = useState('');
  const [selectedClusterSlug, setSelectedClusterSlug] = useState('');
  const [running, setRunning] = useState(false);

  const refresh = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      // Spec 125 — load the trigger inputs (personas + clusters) alongside the
      // ranked diagnoses so admins can enqueue a new diagnosis from this page.
      const [data, p, c] = await Promise.all([
        listPersonaDiagnosesRanked(brandId, { limit: 100 }),
        listPersonas(brandId).catch(() => []),
        listFanoutClusters(brandId).catch(() => []),
      ]);
      setItems(data);
      setPersonas(p);
      setClusters(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load diagnoses');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onRun = useCallback(async () => {
    if (!brandId || !selectedPersona || !selectedClusterSlug) return;
    setRunning(true);
    try {
      // The cluster picker carries slugs; resolve to the UUID the diagnosis
      // route expects (read-only against the 086 fan-out detail route).
      const detail = await getFanoutCluster(brandId, selectedClusterSlug);
      await createPersonaDiagnosis(brandId, {
        persona_id: selectedPersona,
        query_cluster_id: detail.id,
      });
      addToast('Persona diagnosis enqueued', 'success');
      await refresh();
    } catch (e) {
      addToast(
        e instanceof Error ? e.message : 'Failed to enqueue diagnosis',
        'error',
      );
    } finally {
      setRunning(false);
    }
  }, [brandId, selectedPersona, selectedClusterSlug, addToast, refresh]);

  return (
    <div className="persona-diagnosis-summary-page hub-page--table">
      <Breadcrumbs
        items={[
          { label: 'Personas', to: '/personas' },
          { label: 'Persona diagnosis' },
        ]}
      />
      <header>
        <h1 className="hub-page__title flex items-center gap-2">
          <Icon name="bx-sitemap" className="text-violet-600" />
          Persona diagnoses
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Across this persona's query clusters — healthy vs diagnosed — ranked by severity
          × confidence. The outcome → cause → fix deliverable lives one click in.
        </p>
      </header>

      {!brandId && (
        <EmptyState
          icon="bx-sitemap"
          title="Select a brand"
          description="Choose a brand to view its persona diagnoses."
        />
      )}

      {brandId && canRun && (
        <div className="run-diagnosis-bar hub-plate flex flex-wrap items-center gap-3">
          <Select
            data-testid="diagnosis-persona-picker"
            className="min-w-56"
            placeholder="Pick a persona…"
            value={selectedPersona}
            onChange={(v) => setSelectedPersona(v)}
            options={personas.map((p) => ({
              value: p.persona_id,
              label: `${p.name} (${p.persona_type})`,
            }))}
          />
          <Select
            data-testid="diagnosis-cluster-picker"
            className="min-w-56"
            placeholder="Pick a query cluster…"
            value={selectedClusterSlug}
            onChange={(v) => setSelectedClusterSlug(v)}
            options={clusters.map((c) => ({
              value: c.slug,
              label: c.semantic_label || c.slug,
            }))}
          />
          <Button
            variant="primary"
            data-testid="run-diagnosis"
            disabled={running || !selectedPersona || !selectedClusterSlug}
            onClick={() => void onRun()}
          >
            {running ? 'Running…' : 'Run diagnosis'}
          </Button>
        </div>
      )}

      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {error && (
        <div
          data-testid="diagnosis-summary-error"
          className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
        >
          {error}
        </div>
      )}

      <div data-testid="persona-diagnosis-summary">
        {!loading && items.length === 0 && brandId && (
          <EmptyState
            icon="bx-sitemap"
            variant="no-results"
            title="No diagnoses yet"
            description="Pick a persona and a query cluster above, then run a diagnosis. The cluster's AI-answer and page persona-fit findings are used as inputs."
          />
        )}
        {items.length > 0 && (
          <RowList>
            {items.map((item) => (
              <DiagnosisCard key={item.id} item={item} />
            ))}
          </RowList>
        )}
      </div>
    </div>
  );
}
